import React, { useEffect, useState } from 'react';
import { fmtMoney } from './format';
import { feesBilledOf } from '../../utils/rollup';

/**
 * The drill-down behind a roll-up figure — one modal, two shapes of payload.
 *
 * FLAT (`modal.rows`): one scope's worth of rows, from a SummaryByYearTable
 * cell (scoped by fiscal year) or a BreakdownByClientTable cell (scoped by
 * client). Renders a single table.
 *
 * GROUPED (`modal.groups`): a MetricTiles tile, which is an all-time figure, so
 * its detail arrives pre-grouped into collapsible fiscal years by
 * utils/rollup.js's tileDrilldown(). Each group heading carries that year's own
 * subtotal; the subtotals add up to the tile.
 *
 * `kind` picks the columns and is the same in both shapes, so a time entry
 * looks identical whichever way you reached it.
 */
export default function DetailModal({ modal, onClose, navigate }) {
  const { year, metric, rows, groups, kind, clientName, scopeLabel } = modal;

  // A tile spans every year, so it has no single year to name. A cell does —
  // by fiscal year, or (from the group breakdown) by client instead. A tile
  // opened while the page is filtered to a date range names that range.
  const scope = clientName || scopeLabel || (groups ? 'All Years' : `FY ${year}`);
  const title = ({
    hours: `Time Entries — ${scope} (Hours)`,
    time: `Time Entries — ${scope} (Billable Time)`,
    invoiced: `Invoices — ${scope}`,
    writeoffs: `Write-Offs — ${scope}`,
    wip: `Work in Progress — ${scope}`,
    ar: `Accounts Receivable — ${scope}`,
  })[metric] || scope;

  const openInvoice = (r) => { onClose(); navigate(`/invoices/${r.id}`); };

  // The tiles and cells that open this are keyboard-reachable, so the way out
  // has to be too — otherwise the only exit is tabbing to the ×.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white', borderRadius: 8, maxWidth: '90vw', maxHeight: '85vh',
          width: 900, overflow: 'hidden', display: 'flex', flexDirection: 'column',
        }}
      >
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>{title}</h3>
          <button onClick={onClose} aria-label="Close" style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 22, color: '#6b7280' }}>×</button>
        </div>
        <div style={{ overflow: 'auto', padding: 16 }}>
          {groups
            ? <GroupedByYear groups={groups} kind={kind} openInvoice={openInvoice} />
            : <KindBody kind={kind} rows={rows} openInvoice={openInvoice} />}
        </div>
      </div>
    </div>
  );
}

function GroupedByYear({ groups, kind, openInvoice }) {
  // The newest year is the one you almost always want, so it starts open; the
  // rest stay shut so an all-time drill-down opens as a readable index.
  const [expanded, setExpanded] = useState(() => new Set(groups.slice(0, 1).map((g) => g.key)));

  if (!groups.length) {
    return <div style={{ color: '#9ca3af', padding: 24, textAlign: 'center' }}>Nothing here.</div>;
  }

  const toggle = (key) => {
    const next = new Set(expanded);
    if (next.has(key)) next.delete(key); else next.add(key);
    setExpanded(next);
  };

  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden' }}>
      {groups.map((g) => {
        const open = expanded.has(g.key);
        const count = kind === 'wip'
          ? g.rows.timeEntries.length + g.rows.invoices.length + g.rows.writeOffs.length
          : g.rows.length;
        return (
          <div key={g.key}>
            <div
              onClick={() => toggle(g.key)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(g.key); } }}
              style={{
                padding: '12px 16px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb',
                cursor: 'pointer', display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', userSelect: 'none',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: '#6b7280', fontSize: 14, width: 14 }}>{open ? '▼' : '▶'}</span>
                <span style={{ fontWeight: 600, color: '#111827' }}>{g.label}</span>
                <span style={{ color: '#6b7280', fontSize: 13 }}>
                  · {count} {count === 1 ? 'item' : 'items'}
                </span>
              </div>
              <div style={{ fontWeight: 600, color: '#111827' }}>{fmtMoney(g.total)}</div>
            </div>
            {open && (
              <div style={{ padding: kind === 'wip' ? '8px 16px 16px' : 0 }}>
                <KindBody kind={kind} rows={g.rows} openInvoice={openInvoice} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** The columns for one kind of row. Shared by both payload shapes. */
function KindBody({ kind, rows, openInvoice }) {
  if (kind === 'time') {
    return (
      <DetailTable
        columns={[
          { header: 'Date', accessor: 'date' },
          { header: 'Hours', render: (r) => parseFloat(r.hours || 0).toFixed(2) },
          { header: 'Code', accessor: 'time_code' },
          { header: 'Who', accessor: 'user_name' },
          { header: 'Amount', render: (r) => fmtMoney(parseFloat(r.billable_amount || 0)) },
          { header: 'Notes', accessor: 'notes' },
        ]}
        rows={rows}
      />
    );
  }
  if (kind === 'invoice') {
    return (
      <DetailTable
        columns={[
          { header: 'Number', accessor: 'number' },
          { header: 'Date', accessor: 'date' },
          { header: 'Status', accessor: 'status' },
          { header: 'Fees billed', render: (r) => fmtMoney(feesBilledOf(r)) },
          { header: 'Discount', render: (r) => fmtMoney(parseFloat(r.discount || 0)) },
          { header: 'Total', render: (r) => fmtMoney(parseFloat(r.total || 0)) },
        ]}
        rows={rows}
        onRowClick={openInvoice}
      />
    );
  }
  if (kind === 'writeoff') {
    return (
      <DetailTable
        columns={[
          { header: 'Date', accessor: 'date' },
          { header: 'Amount', render: (r) => fmtMoney(parseFloat(r.amount || 0)) },
          { header: 'Reason', accessor: 'reason' },
        ]}
        rows={rows}
      />
    );
  }
  if (kind === 'wip') {
    return (
      <div>
        <h4 style={{ marginTop: 0 }}>Time Entries</h4>
        <DetailTable
          columns={[
            { header: 'Date', accessor: 'date' },
            { header: 'Hours', render: (r) => parseFloat(r.hours || 0).toFixed(2) },
            { header: 'Who', accessor: 'user_name' },
            { header: 'Amount', render: (r) => fmtMoney(parseFloat(r.billable_amount || 0)) },
          ]}
          rows={rows.timeEntries}
        />
        <h4 style={{ marginTop: 24 }}>Invoices</h4>
        <DetailTable
          columns={[
            { header: 'Number', accessor: 'number' },
            { header: 'Date', accessor: 'date' },
            { header: 'Fees billed', render: (r) => fmtMoney(feesBilledOf(r)) },
            { header: 'Discount', render: (r) => fmtMoney(parseFloat(r.discount || 0)) },
          ]}
          rows={rows.invoices}
          onRowClick={openInvoice}
        />
        {rows.writeOffs.length > 0 && (
          <>
            <h4 style={{ marginTop: 24 }}>Write-Offs</h4>
            <DetailTable
              columns={[
                { header: 'Date', accessor: 'date' },
                { header: 'Amount', render: (r) => fmtMoney(parseFloat(r.amount || 0)) },
                { header: 'Reason', accessor: 'reason' },
              ]}
              rows={rows.writeOffs}
            />
          </>
        )}
      </div>
    );
  }
  return null;
}

function DetailTable({ rows, columns, onRowClick }) {
  if (!rows || rows.length === 0) {
    return <div style={{ color: '#9ca3af', padding: 24, textAlign: 'center' }}>Nothing here.</div>;
  }
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
          {columns.map((c, i) => (
            <th key={i} style={{ padding: '8px 10px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{c.header}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr
            key={r.id ?? i}
            onClick={onRowClick ? () => onRowClick(r) : undefined}
            style={{ borderBottom: '1px solid #f3f4f6', cursor: onRowClick ? 'pointer' : 'default' }}
          >
            {columns.map((c, j) => (
              <td key={j} style={{ padding: '8px 10px', fontSize: 13 }}>
                {c.render ? c.render(r) : (r[c.accessor] ?? '—')}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
